import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { createSigner, getBusiness } from 'lib/api';
import CustomButton from 'lib/designSystem/Button';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import {
  rutChanger,
  showAlert,
  validaRut,
  validateChileanPhone,
} from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

interface CreateSignerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  fetchSigners: () => Promise<void>;
  businessIdentifier?: string;
}

const CreateSigner = ({
  open,
  setOpen,
  fetchSigners,
  businessIdentifier,
}: CreateSignerProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [identifier, setIdentifier] = useState('');
  interface InputErrors {
    name: string;
    email: string;
    phone: string;
    identifier: string;
  }
  const [errors, setErrors] = useState<InputErrors>({
    name: '',
    email: '',
    phone: '',
    identifier: '',
  });

  const validateField = (field: keyof InputErrors, value: string): string => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (field === 'name') {
      return value.trim() ? '' : 'Name is required';
    }
    if (field === 'email') {
      return emailRegex.test(value) ? '' : 'Please enter a valid email';
    }
    if (field === 'phone') {
      return validateChileanPhone(value)
        ? ''
        : 'Phone must be a valid Chilean number';
    }
    if (field === 'identifier') {
      return validaRut(value) ? '' : 'RUT is invalid. Use 12345678-K format';
    }
    return '';
  };

  const handleBlur = (field: keyof InputErrors, value: string) => {
    const message = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const handleSaveSigner = async () => {
    try {
      setErrors({ name: '', email: '', phone: '', identifier: '' });

      const nextErrors: InputErrors = {
        name: validateField('name', name),
        email: validateField('email', email),
        phone: validateField('phone', phone),
        identifier: validateField('identifier', identifier),
      };

      const hasErrors = Object.values(nextErrors).some(Boolean);
      if (hasErrors) {
        setErrors(nextErrors);
        return;
      }

      if (!businessIdentifier) {
        return showAlert(dispatch, 'error', 'Problemas al obtener la empresa');
      }

      setLoading(true);
      const business = await getBusiness(dispatch, businessIdentifier);
      await createSigner(dispatch, {
        email: email,
        name: name,
        phone: phone,
        identifier,
        businessIdentifier,
        businessName: business.name,
      });
      void fetchSigners();
      showAlert(dispatch, 'success', 'Signer created successfully');
      setOpen(false);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>
        <Title
          title="Crear firmante"
          icon={<PersonIcon color="primary" />}
          color="primary"
        />
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 1,
            minWidth: 420,
          }}
        >
          <TextField
            label="Rut"
            placeholder="12345678-K"
            value={identifier}
            onChange={(e) => {
              rutChanger(e.target.value, (val) => {
                setIdentifier(val);
                setErrors((prev) => ({
                  ...prev,
                  identifier: validateField('identifier', val),
                }));
              });
            }}
            onBlur={(e) => handleBlur('identifier', e.target.value)}
            fullWidth
            error={Boolean(errors.identifier)}
            helperText={errors.identifier}
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              const val = e.target.value;
              setName(val);
              setErrors((prev) => ({
                ...prev,
                name: validateField('name', val),
              }));
            }}
            onBlur={(e) => handleBlur('name', e.target.value)}
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name}
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              const val = e.target.value;
              setEmail(val);
              setErrors((prev) => ({
                ...prev,
                email: validateField('email', val),
              }));
            }}
            onBlur={(e) => handleBlur('email', e.target.value)}
            fullWidth
            error={Boolean(errors.email)}
            helperText={errors.email}
          />
          <TextField
            label="Phone"
            placeholder="56912345678"
            value={phone}
            onChange={(e) => {
              const val = e.target.value.replace(/\s/g, '');
              setPhone(val);
              setErrors((prev) => ({
                ...prev,
                phone: validateField('phone', val),
              }));
            }}
            onBlur={(e) => handleBlur('phone', e.target.value)}
            fullWidth
            error={Boolean(errors.phone)}
            helperText={errors.phone}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <CustomButton
          onClick={handleSaveSigner}
          color="primary"
          loading={loading}
        >
          Agregar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSigner;
